import { Link } from 'react-router-dom';
import React, { useMemo } from 'react';
import { Button } from './Button';

export interface BoxProps {
  title: string;
  href: string;
  imageUrl: string;
  startDate: string;
  endDate: string;
  count: string;
  ownerId: string;
}

export interface BoxesProps {
  trips?: BoxProps[];
  createBoxClick?: () => void | Promise<void>;
  useReactNavigation?: boolean;
}

export const TripBoxes = React.memo((props: BoxesProps) => {
  const trips = props.trips ? props.trips : [];

  const groupTripsByYear = (trips: BoxProps[]) => {
    const grouped = trips.reduce((acc, trip) => {
      const year = new Date(trip.startDate).getFullYear().toString();
      if (!acc[year]) {
        acc[year] = [];
      }
      acc[year].push(trip);
      return acc;
    }, {} as Record<string, BoxProps[]>);

    Object.keys(grouped).forEach((year) => {
      grouped[year].sort(
        (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
      );
    });

    return Object.keys(grouped)
      .sort((a, b) => parseInt(a) - parseInt(b))
      .reduce((acc, year) => {
        acc[year] = grouped[year];
        return acc;
      }, {} as Record<string, BoxProps[]>);
  };

  const tripsByYear = useMemo(() => groupTripsByYear(trips), [trips]);

  return (
    <div className="w-full">
      {props.createBoxClick && (
        <div className="mb-8 flex justify-end">
          <Button
            variant="primary"
            size="md"
            onClick={props.createBoxClick}
            className="rounded-full shadow-lg"
          >
            + New Trip
          </Button>
        </div>
      )}

      {Object.entries(tripsByYear)
        .reverse()
        .map(([year, yearTrips]) => (
          <div key={year} className="mb-16">
            <div className="flex items-center mb-8">
              <h2 className="text-3xl font-extrabold text-main tracking-tight">{year}</h2>
              <div className="ml-6 flex-1 border-t-2 border-subtle"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
              {yearTrips.map((trip, index) => (
                <Link
                  key={`${year}-${index}`}
                  to={trip.href}
                  className="flex flex-col group relative cursor-pointer bg-surface rounded-3xl p-3 shadow-sm ring-1 ring-gray-900/5 hover:shadow-xl hover:ring-gray-900/10 hover:-translate-y-1 transition-all duration-300"
                  {...(!props.useReactNavigation && {
                    onClick: (e) => {
                      e.preventDefault();
                      window.location.href = trip.href;
                    },
                  })}
                >
                  <div className="relative w-full overflow-hidden rounded-2xl">
                    <img
                      alt=""
                      src={trip.imageUrl}
                      className="aspect-[3/2] w-full bg-muted object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => (e.currentTarget.src = '/default-trip-header.jpg')}
                    />
                    <div className="absolute inset-0 ring-1 ring-inset ring-black/5 rounded-2xl" />
                    {trip.count && (
                      <div className="absolute top-3 right-3 z-10 rounded-full bg-surface/90 px-3 py-1 text-xs font-bold text-main shadow-sm backdrop-blur-md">
                        {trip.count}
                      </div>
                    )}
                  </div>
                  <div className="mt-4 px-2 pb-2">
                    <div className="flex items-center gap-x-4">
                      <img
                        src={`https://media.${process.env.REACT_APP_HOST}/users/${
                          trip.ownerId
                        }-thumbnail.jpg?v=${Date.now()}`}
                        alt=""
                        className="h-10 w-10 rounded-full bg-subtle ring-2 ring-white shadow-sm object-cover"
                        onError={(e) => (e.currentTarget.src = '/default-profile-picture.jpg')}
                      />
                      <div className="flex flex-col">
                        <h3 className="text-lg font-bold text-main group-hover:text-brand transition-colors">
                          {trip.title}
                        </h3>
                        {trip.startDate && trip.endDate && (
                          <div className="flex items-center gap-x-1 text-xs font-medium text-muted">
                            <time dateTime={trip.startDate}>{trip.startDate}</time>
                            <span>•</span>
                            <time dateTime={trip.endDate}>{trip.endDate}</time>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
});
